import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { PageHeader } from "@/components/ui-kit";
import { toast } from "sonner";

const PILL_STYLES = {
  trial: "bg-teal-50 text-teal-800 border-teal-200",
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  past_due: "bg-amber-50 text-amber-800 border-amber-200",
  canceled: "bg-rose-50 text-rose-800 border-rose-200",
  internal: "bg-slate-100 text-slate-700 border-slate-300",
};

const STATUS_LABELS = {
  trial: "Trial",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
  internal: "Internal",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function fmtCents(cents) {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function statusSentence(sub) {
  if (!sub) return "";
  const { status, locked, trial_ends_at, days_left } = sub;
  if (status === "trial") {
    if (locked) {
      return `Your trial ended on ${fmtDate(trial_ends_at)}. The workspace is read-locked until the subscription starts.`;
    }
    const days = typeof days_left === "number" ? days_left : 0;
    return `Your free trial ends on ${fmtDate(trial_ends_at)}, ${days} day${days === 1 ? "" : "s"} left.`;
  }
  if (status === "active") return "Active, billed monthly.";
  if (status === "past_due") return "Payment is past due.";
  if (status === "canceled") {
    return locked
      ? "Your subscription was canceled. The workspace is read-locked until the subscription starts again."
      : "Your subscription was canceled.";
  }
  if (status === "internal") return "Internal tenant, no billing.";
  return "";
}

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/agency/subscription")
      .then((r) => setSub(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const status = sub?.status || "trial";
  const pillClass = PILL_STYLES[status] || PILL_STYLES.trial;
  const seats = sub?.seats ?? 0;

  return (
    <div data-testid="subscription-page">
      <PageHeader subtitle="Your plan and seats" title="Subscription" />

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid gap-5 max-w-2xl">
          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Plan</div>
            <span
              className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border ${pillClass}`}
              data-testid="subscription-status"
            >
              {STATUS_LABELS[status] || status}
            </span>
            <p className="text-sm text-slate-700 mt-3">{statusSentence(sub)}</p>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Seats and price</div>
            <div className="flex items-baseline gap-2">
              <div className="font-display text-2xl font-bold text-[#0A192F]" data-testid="subscription-seats">
                {seats}
              </div>
              <div className="text-sm text-slate-600">active vehicle{seats === 1 ? "" : "s"}</div>
            </div>
            <div className="text-sm text-slate-600 mt-2">$39 per vehicle per month</div>
            <div className="text-sm text-slate-900 font-medium mt-1">
              Estimated monthly total: <span data-testid="subscription-estimate">{fmtCents(sub?.monthly_estimate_cents)}</span>
            </div>
            <div className="text-xs text-slate-500 mt-3">Unlimited drivers, rides and facility logins. No per-ride fees.</div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">
              Start or change your subscription
            </div>
            <a
              href={`mailto:${sub?.contact_email || "sales@ridewisehealth.com"}?subject=${encodeURIComponent(
                `${BRAND} subscription for ${sub?.agency_name || ""}`
              )}`}
              className="btn-primary inline-flex items-center"
              data-testid="subscription-contact"
            >
              Email sales to start
            </a>
            <p className="text-xs text-slate-500 mt-3">
              Online checkout is coming; until then we set your plan up by hand within one business day.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
